import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@components/atoms/ui-kit/button.tsx';
import Loader from '@components/atoms/loaders/loader.tsx';
import { AccessTier, Session } from '@utils/types/editors/sessions.ts';
import { useRehydrateSession } from '@utils/api/sessions/use-rehydrate-session.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';

const ARCHIVE_TIMEOUT_MS = 90 * 60 * 1000; // 90 min hard cap

type Props = {
  session: Session;
  /** Hide the manual "Restore" CTA — used on shared (unauthenticated) editors. */
  readOnly?: boolean;
};

type Phase = 'cold' | 'archive-idle' | 'archive-active' | 'archive-timeout';

function resolvePhase(
  session: Session,
  startedAt: number | null
): Phase | null {
  const tier: AccessTier = session.access_tier ?? 'hot';
  if (tier === 'hot' || tier === 'cool') return null;
  if (tier === 'cold') return 'cold';

  // archive
  const isActive = !!session.archive_status;
  if (!isActive) return 'archive-idle';
  if (startedAt && Date.now() - startedAt > ARCHIVE_TIMEOUT_MS) {
    return 'archive-timeout';
  }
  return 'archive-active';
}

function formatEta(seconds?: number | null): string | null {
  if (!seconds || seconds <= 0) return null;
  if (seconds < 120) return `~${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `~${minutes} min`;
  const hours = Math.round(minutes / 6) / 10;
  return `~${hours}h`;
}

const BlobWarmingOverlay = ({ session, readOnly }: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const rehydrate = useRehydrateSession(session.session_id);
  const [archiveStartedAt, setArchiveStartedAt] = useState<number | null>(null);

  // Track when we first saw an active archive rehydration so we can time-out.
  useEffect(() => {
    if (
      session.access_tier === 'archive' &&
      session.archive_status &&
      archiveStartedAt === null
    ) {
      setArchiveStartedAt(Date.now());
    }
    if (session.access_tier !== 'archive' || !session.archive_status) {
      setArchiveStartedAt(null);
    }
  }, [session.access_tier, session.archive_status, archiveStartedAt]);

  const phase = useMemo(
    () => resolvePhase(session, archiveStartedAt),
    [session, archiveStartedAt]
  );

  if (!phase) return null;

  const eta = formatEta(session.rehydration_eta_seconds);

  let title = '';
  let message = '';
  let cta: React.ReactNode = null;

  switch (phase) {
    case 'cold':
      title = t('warming-up-file');
      message = t('restoring-from-cold-storage-typically-1-3-minutes');
      break;
    case 'archive-idle':
      title = t('file-in-long-term-storage');
      message = t('this-file-was-archived-restore-takes-about-1-hour');
      if (!readOnly) {
        cta = (
          <Button
            onClick={() => rehydrate.mutate()}
            disabled={rehydrate.isLoading}
          >
            {rehydrate.isLoading ? t('starting') : t('restore-file')}
          </Button>
        );
      } else {
        message = t(
          'this-file-was-archived-please-ask-the-owner-to-restore-it'
        );
      }
      break;
    case 'archive-active':
      title = t('restoring-from-archive');
      message = eta
        ? t('restoring-from-archive-eta', { eta })
        : t('restoring-from-archive-message');
      break;
    case 'archive-timeout':
      title = t('still-warming-up');
      message = t('restore-is-taking-longer-than-expected-try-again-later');
      if (!readOnly) {
        cta = (
          <Button
            onClick={() => {
              setArchiveStartedAt(Date.now());
              rehydrate.mutate();
            }}
            disabled={rehydrate.isLoading}
          >
            {t('retry')}
          </Button>
        );
      }
      break;
  }

  const showSpinner = phase === 'cold' || phase === 'archive-active';

  return (
    <div
      className="flex items-center justify-center h-screen"
      style={{ backgroundColor: getColour('baseLayer') }}
    >
      <div
        className="text-center p-8 rounded-2xl shadow-md max-w-lg w-full mx-3 border"
        style={{
          backgroundColor: getColour('thirdLayer'),
          borderColor: getColour('border')
        }}
      >
        {showSpinner && (
          <div className="flex justify-center mb-4">
            <Loader />
          </div>
        )}
        <h1
          className="text-2xl font-bold mb-3"
          style={{ color: getColour('normalText') }}
        >
          {title}
        </h1>
        <p className="mb-6" style={{ color: getColour('inactiveText') }}>
          {message}
        </p>
        {cta}
        {rehydrate.isError && (
          <p className="mt-4 text-sm text-orange-600 dark:text-orange-400">
            {t('an-error-has-occurred')}
          </p>
        )}
      </div>
    </div>
  );
};

export default BlobWarmingOverlay;
